import { Box, Button, CircularProgress, Grid, List, ListItem, ListItemText, makeStyles, Toolbar, Typography } from '@material-ui/core';
import { format } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TaskProps } from '.';
import { DynamicForm, LanguageString } from '..';
import * as DTO from '../../dto';
import { useFormAndSubmission, useFormSubmissions, useResetCompletedAssignment } from '../../hooks';

const dt = (dateStr: string) => format(new Date(dateStr), 'MM/dd/yy h:mm a');
const submissionDateText = (fs: DTO.FormSubmission) =>
  fs.completedAt != null ? `${dt(fs.completedAt)}` : fs.startedAt != null ? `${dt(fs.startedAt)} Previous Save` : 'No Date';

const useStyles = makeStyles(() => ({
  tbPrevSub: {
    flexGrow: 1
  }
}));

const toSubmitFormInput = (fs: DTO.FormSubmission): DTO.SubmitFormInput => ({
  formId: fs.form.id,
  answers: fs.answers.map(a => ({
    questionId: a.question.id,
    selections: a.selections?.map(s => s.id),
    text: a.answer
  }))
});

const CompletedByInfo: React.FC<{submissions: DTO.FormSubmission[], selectedSubmission?: DTO.FormSubmission}> = ({submissions, selectedSubmission}) => {
  const submission = selectedSubmission ?? submissions.length ? submissions[0] : undefined;

  const completedOnString = submissions.filter(s => s.completedAt != null).length < 2
    ? <LanguageString groupName="COMPLETED_INFO" resourceName="ON" style={{ fontWeight: 'bold', display: 'inline-block' }} />
    : <LanguageString groupName="COMPLETED_INFO" resourceName="LAST_ON" style={{ fontWeight: 'bold', display: 'inline-block' }} />;

  return submission?.completedAt ? <div>
    {submission.submittedBy && <div><LanguageString groupName="COMPLETED_INFO" resourceName="BY" style={{ fontWeight: 'bold', display: 'inline-block'}} /> <Typography style={{ display: 'inline-block' }}>{submission.submittedBy.firstName} {submission.submittedBy.lastName}</Typography></div>}
    {completedOnString} <Typography style={{ display: 'inline-block' }}>{new Date(submission.completedAt).toDateString()}</Typography>
  </div> : null;
};

export const FormTask: React.FC<TaskProps & { formId: number }> = ({ user, addButtons, student, assignment, formId, onComplete }) => {
  const loc = useLocation();
  const nocache = new URLSearchParams(loc.search).get('nocache');
  const { data: fData } = useFormAndSubmission({ variables: { formId } });
  const variables: Parameters<typeof useFormSubmissions>[0]['variables'] = {
    userId: student?.id ?? 0,
    formId,
    assignmentId: assignment?.id,
    includes: DTO.IncludeSubmissionTypes.SUBMITTED_AND_LAST_SAVED
  };
  const [lastSubmissionId, setLastSubmissionId] = useState<number>();
  const { data, loading } = useFormSubmissions({
    fetchPolicy: 'cache-and-network',
    variables,
    onCompleted: (td) => {
      const lastFsId = td.user?.formSubmissions?.length ? td.user.formSubmissions[0].id : undefined;
      if (lastFsId != null) setLastSubmissionId(lastFsId);
    }
  });

  const addNewClicked = useRef(false);
  const addReviseClicked = useRef(false);

  const [selectedSubmission, setSelectedSubmission] = useState<DTO.FormSubmission | undefined>(data?.user?.formSubmissions?.[0]);
  const fsSpecified = useRef(false);
  const fsLengthRef = useRef(data?.user?.formSubmissions?.length);

  const classes = useStyles();

  const [resetAssignment] = useResetCompletedAssignment({ refetchQueries: ['getSubmissions'] });

  const handleSelectFs = (fs: DTO.FormSubmission | undefined) => {
    fsSpecified.current = true;
    setSelectedSubmission(fs);
  };

  const [orFsData, setOrFsData] = useState<DTO.SubmitFormInput>();

  const handleAddNewClick = () => {
    setOrFsData({ formId });
    addNewClicked.current = true;
    addReviseClicked.current = false;
    handleSelectFs(undefined);
  };

  const handleAddReviseClick = () => {
    if (selectedSubmission) setOrFsData(toSubmitFormInput(selectedSubmission));
    addNewClicked.current = false;
    addReviseClicked.current = true;
    handleSelectFs(undefined);
  };

  const handleResetClick = () => (assignment != null ? resetAssignment({ variables: { input: { assignmentId: assignment.id } } }) : null);

  useEffect(() => {
    if (user.id !== student?.id && user.roles?.includes(DTO.Role.ADVISOR)) {
      addButtons([
        <Button key="1" onClick={handleResetClick}>
          <LanguageString groupName="FORM_LABELS_ERRORS" resourceName="RESET" defaultText="Reset" />
        </Button>
      ]);
    } else {
      addButtons([]);
    }
  }, [formId]);

  const form = fData?.form;
  const formSubmissions = data?.user?.formSubmissions != null ? [...data?.user?.formSubmissions] : [];
  const prevSaved = formSubmissions.length > 0 && formSubmissions[0].completedAt == null;

  const curSubsLen = fsLengthRef.current;
  if (formSubmissions?.length) {
    if (
      curSubsLen !== formSubmissions.length ||
      (!fsSpecified.current && (selectedSubmission == null || !formSubmissions.includes(selectedSubmission)))
    ) {
      fsLengthRef.current = formSubmissions.length;
      setSelectedSubmission(formSubmissions[0]);
    }
  }

  if (loading && data?.user == null) {
    return (
      <Grid container justify="center" alignItems="center">
        <Grid item>
          <CircularProgress color="primary" />
        </Grid>
      </Grid>
    );
  }

  return form == null ? null : (
    <Box>
      <CompletedByInfo submissions={formSubmissions} selectedSubmission={selectedSubmission} />
      {selectedSubmission?.completedAt != null ?
        <DynamicForm
          formInst={form}
          formSubmissionInst={selectedSubmission}
          userId={student?.id}
          disabled={true}
          disableCache={Boolean(nocache)}
          autoloadFormSubmission={true}
          onFormSubmitted={() => onComplete?.()}
          onError={() => onComplete?.()}
          showSubmitButton={false}
          autoSave={false}
          disabledDownloadButton={true}
          previousSubmissionId={lastSubmissionId}
          overrideFormSubmissionInput={orFsData}
        /> :
        <DynamicForm
          formId={formId}
          userId={student?.id}
          assignmentId={assignment?.id}
          disabled={assignment == null}
          disableCache={Boolean(nocache)}
          autoloadFormSubmission={formSubmissions?.[0] == null && assignment?.id != null && true}
          formSubmissionInst={!orFsData ? formSubmissions?.[0]: undefined}
          onFormSubmitted={() => onComplete?.()}
          onError={() => onComplete?.()}
          showSubmitButton={false}
          autoSave={true}
          disabledDownloadButton={true}
          previousSubmissionId={lastSubmissionId}
          overrideFormSubmissionInput={orFsData}
        />
      }
      {formSubmissions?.length && form.allowMultipleSubmissions ? (
        <>
          <Box bgcolor="primary.light" color="primary.contrastText" mt="20px">
            <Toolbar>
              <LanguageString className={classes.tbPrevSub}  groupName="SITE_CONTENT" resourceName="PREV_SUBMISSIONS" />
              {!form.reviseRestricted &&
                <>
                  <Button color="inherit" onClick={handleAddNewClick}>Blank</Button>
                  <Button color="inherit" onClick={handleAddReviseClick}>Revise</Button>
                </>
              }
            </Toolbar>
          </Box>
          <List>
            {!prevSaved && addNewClicked.current &&
              <ListItem button selected={selectedSubmission == null}>
                <ListItemText primary="New" />
              </ListItem>
            }
            {!prevSaved && addReviseClicked.current &&
              <ListItem button selected={selectedSubmission == null}>
                <ListItemText primary="New" />
              </ListItem>
            }
            {formSubmissions.map(fs => (
              <ListItem key={fs.id} button selected={fs.id === selectedSubmission?.id} onClick={() => handleSelectFs(fs)}>
                <ListItemText primary={submissionDateText(fs)} />
              </ListItem>
            ))}
          </List>
        </>
      ) : null}
    </Box>
  );
};
